import {
  differenceInDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  isSameDay,
  isSameMonth,
  isSameWeek,
} from 'date-fns';
import moment from 'moment';

const colors = ['#5B8FF9', '#30BF78', '#5D7092', '#F6BD16', '#E8684A', '#6DC8EC', '#945FB9', '#FF9845', '#1E9493'];

export const calculateDatasets = ({ graphData, startDate, endDate }) => {
  if (!graphData) return [];

  const data = Object.entries(graphData);

  const datasets = data?.map(([key, value], index) => ({
    label: key,
    data: [],
    backgroundColor: colors[index % colors.length],
  }));

  const step = calculateLabelsStep(startDate, endDate);
  const isSame = getIsSameStepFunction(step);

  const labels = calculateLabels(startDate, endDate, false);

  labels.forEach(label => {
    data.forEach(([key, value], index) => {
      const total = value.reduce((acc, result) => {
        // Handle various date formats
        if (!result?.date) return acc;

        // Resolve to a local-midnight Date so isSameDay/isSameWeek/isSameMonth work correctly.
        // Date-only strings (YYYY-MM-DD) are parsed as local midnight to avoid UTC shift.
        // Full UTC timestamps (e.g. "2026-05-03T04:00:00.000Z") are converted to their
        // local calendar date before comparison so evening appointments don't land on the next day.
        let resultDate;
        if (result.date instanceof Date) {
          resultDate = new Date(result.date.getFullYear(), result.date.getMonth(), result.date.getDate());
        } else {
          const dateStr = String(result.date);
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [y, m, d] = dateStr.split('-').map(Number);
            resultDate = new Date(y, m - 1, d);
          } else {
            const parsed = new Date(dateStr);
            resultDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
          }
        }

        if (isSame(resultDate, label)) {
          return (parseFloat(acc) + parseFloat(result.total)).toFixed(2);
        }
        return acc;
      }, 0);
      datasets[index].data.push(total);
    });
  });

  return datasets;
};

export const calculateLabelsStep = (startDate, endDate) => {
  const difference = differenceInDays(endDate, startDate);
  if (difference < 13) {
    return 'days';
  }
  if (difference < 120) {
    return 'weeks';
  }
  return 'months';
};

const getIsSameStepFunction = step => {
  switch (step) {
    case 'days':
      return isSameDay;
    case 'weeks':
      return isSameWeek;
    case 'months':
      return isSameMonth;
    default:
      return () => false;
  }
};

export const calculateLabels = (startDate, endDate, formatted = true) => {
  if (!startDate || !endDate) return [];
  const step = calculateLabelsStep(startDate, endDate);
  let labels = [];
  switch (step) {
    case 'days':
      labels = eachDayOfInterval({ start: startDate, end: endDate });
      return formatted ? labels.map(date => moment(date).format('MMM DD')) : labels;
    case 'weeks':
      labels = eachWeekOfInterval({ start: startDate, end: endDate });
      return formatted ? labels.map(date => moment(date).format('MMM DD')) : labels;
    case 'months':
      labels = eachMonthOfInterval({ start: startDate, end: endDate });
      return formatted ? labels.map(date => moment(date).format('MMM YYYY')) : labels;
    default:
      return [];
  }
};

export const calculateStep = datasets => {
  let maxVal = 0;
  datasets.forEach(data => {
    data.data.forEach(val => {
      if (val > maxVal) {
        maxVal = val;
      }
    });
  });
  if (maxVal <= 20) {
    return 5;
  }
  if (maxVal < 100) {
    return 10;
  }
  if (maxVal > 500) {
    return 25;
  }
  if (maxVal > 1000) {
    return 100;
  }
  return 100;
};
